import { Injectable } from '@angular/core';
import {
  Idrv5QuoteDetailsResponse,
  Idrv5QuotePackaging,
  Idrv5QuoteRateEntry,
  Idrv5DgLicense
} from './idrv5-api.service';

@Injectable({
  providedIn: 'root'
})
export class Idrv5MapperService {

  private readonly DG_TYPE_PACKED = 1;  // matches BookingComponent.DG_TYPE.PACKED

  constructor() { }

  /**
   * Map an iDrv5 GET /api/get_quote_details/:id response to the internal
   * booking format used by BookingComponent / patchQuoteDataToForm().
   *
   * Selection priority for the active rate entry:
   *   1. service_level_id from the URL query param (highest priority)
   *   2. is_selected: true in the API response
   *   3. First entry in quote_rate_entries (last resort)
   *
   * @param response       Full response from the iDrv5 API
   * @param serviceLevelId Optional — service_level_id from the URL query param
   */
  mapFromQuoteResponse(
    response: Idrv5QuoteDetailsResponse,
    serviceLevelId?: string | number
  ): any {
    const quote = response.quote;

    // ------------------------------------------------------------------
    // 1. Select the rate entry
    //
    //   Two distinct modes based on which URL params are present:
    //
    //   A) quote_id_widget + service_level_id → match by service_level_id.
    //      Falls back to first entry only if no match is found (warn logged).
    //
    //   B) quote_id_widget only (no service_level_id) → use is_selected.
    //      Falls back to first entry if none is flagged as selected.
    // ------------------------------------------------------------------
    let selectedRateEntry: Idrv5QuoteRateEntry | null = null;

    if (serviceLevelId !== undefined && serviceLevelId !== null) {
      // ── Mode A: service_level_id present in URL ──────────────────────
      selectedRateEntry = response.quote_rate_entries.find(
        r => String(r.service_level_id) === String(serviceLevelId)
      ) || null;

      if (selectedRateEntry) {
        console.log(`iDrv5: [mode A] selected rate entry by service_level_id=${serviceLevelId}:`, selectedRateEntry);
      } else {
        console.warn(`iDrv5: [mode A] no rate entry found for service_level_id=${serviceLevelId}, falling back to first entry`);
        selectedRateEntry = response.quote_rate_entries[0] || null;
      }
    } else {
      // ── Mode B: quote_id_widget only — honour is_selected ────────────
      selectedRateEntry = response.quote_rate_entries.find(r => r.is_selected) || null;
      if (selectedRateEntry) {
        console.log(`iDrv5: [mode B] selected rate entry by is_selected:`, selectedRateEntry);
      }

      if (!selectedRateEntry && response.quote_rate_entries.length > 0) {
        console.warn(`iDrv5: [mode B] no is_selected entry found, displaying 0.00 — service_level_id required to select a rate`);
        selectedRateEntry = null;
      }
    }

    // ------------------------------------------------------------------
    // 2. Price — always read rate_entry_grand_amount from the selected
    //    rate entry. Which entry is selected depends on the URL params:
    //
    //   • quote_id_widget only        → entry where is_selected = true
    //   • quote_id_widget + service_level_id → entry matched by service_level_id
    //
    //   Displays 0.00 if rate_entry_grand_amount is null/absent.
    // ------------------------------------------------------------------
    const grandAmount = selectedRateEntry?.rate_entry_grand_amount ?? null;
    const estTotalPrice = grandAmount !== null && grandAmount !== undefined
      ? parseFloat(String(grandAmount))
      : 0;

    if (grandAmount !== null && grandAmount !== undefined) {
      console.log(`iDrv5: price source = rate_entry_grand_amount, value=${estTotalPrice}`);
    } else {
      console.warn(`iDrv5: rate_entry_grand_amount is null, displaying 0.00`);
    }

    // ------------------------------------------------------------------
    // 3. Service option — derived from the selected rate entry.
    //    When service_level_id was provided in the URL, selectedRateEntry
    //    already reflects that choice, so we read directly from it.
    // ------------------------------------------------------------------
    const serviceOption = this.getServiceOption(
      selectedRateEntry?.service_level_id,
      selectedRateEntry?.service_level_name,
      selectedRateEntry?.service_level_description
    );

    // ------------------------------------------------------------------
    // 4. Packagings — iDrv5 already provides packaging_type_id directly.
    //    If it is missing or 0, return null so Validators.required forces
    //    the user to re-select.
    //    When is_oversized is true, use Oversized Plain Pallet (ID 19).
    // ------------------------------------------------------------------
    const packagings = response.quote_packagings.map((pkg, index) => {
      let resolvedTypeId: number | null;
      
      // Check if is_oversized flag is true
      if (pkg.is_oversized === true) {
        resolvedTypeId = 19; // Oversized Plain Pallet
        console.log(`iDrv5: packaging #${index + 1} marked as oversized, using Oversized Plain Pallet (ID 19)`);
      } else {
        resolvedTypeId = this.resolvePackagingTypeId(pkg);
        if (resolvedTypeId === null) {
          console.warn(
            `iDrv5: packaging #${index + 1} has unrecognised type ` +
            `"${pkg.packaging_type_name}" (id=${pkg.packaging_type_id}) — ` +
            `user must select packaging type manually`
          );
        }
      }

      const qty    = pkg.quantity || 1;
      const weight = parseFloat(pkg.weight || '0');
      const length = parseFloat(pkg.length || '0');
      const width  = parseFloat(pkg.width  || '0');
      const height = parseFloat(pkg.height || '0');

      return {
        id: index + 1,
        outside_quote_reference: String(quote.id),
        packaging_type_id:   resolvedTypeId,   // null → required field, user must pick
        packaging_type_name: pkg.packaging_type_name,
        quantity:   qty,
        length:     length,
        width:      width,
        height:     height,
        weight:     weight,
        total_weight: weight * qty,
        chargeablePallets: this.calculateChargeablePallets(length, width, height, weight, qty),
        updated_by: null
      };
    });

    // ------------------------------------------------------------------
    // 5. Totals
    // ------------------------------------------------------------------
    const totalWeight = packagings.reduce((sum, p) => sum + p.total_weight, 0);
    const chargeablePallets = Math.ceil(
      packagings.reduce((sum, p) => sum + p.chargeablePallets, 0)
    );

    // ------------------------------------------------------------------
    // 6. DG flag — true if any packaging has dg_licenses.
    //    Collect all unique dg_license_ids across all packagings.
    //    The first license becomes the main dg_class_id; the rest are
    //    subsidiaries — matching how the booking form models DG classes.
    // ------------------------------------------------------------------
    const allDgLicenses = response.quote_packagings
      .reduce((acc, p) => acc.concat(p.dg_licenses || []), [] as Idrv5DgLicense[]);
    const isDg = allDgLicenses.length > 0;

    const mainDgLicense = allDgLicenses[0] || null;
    const subsidiaryDgLicenses = allDgLicenses.slice(1);

    const dgClass = isDg ? {
      dg_class_id: mainDgLicense ? String(mainDgLicense.dg_license_id) : '',
      dg_type_id:  String(this.DG_TYPE_PACKED),   // default to PACKED (1)
      subsidiaries: subsidiaryDgLicenses.map(l => String(l.dg_license_id))
    } : null;

    // ------------------------------------------------------------------
    // 7. Taillift and Premise Type flags from the quote header
    //
    //    The iDrv5 API now returns origin_premise_type and
    //    destination_premise_type as direct premise_types table IDs:
    //      1 = Residential
    //      2 = Commercial
    //      3 = Depot
    //    These are used as-is — no boolean conversion needed.
    // ------------------------------------------------------------------
    const pickupIsTailLift = !!(quote.origin_is_taillift);
    const deliveryIsTailLift = !!(quote.destination_is_taillift);

    // Use premise_type IDs directly from the API response
    const pickupPremiseTypeId = quote.origin_premise_type || 2;       // default Commercial
    const deliveryPremiseTypeId = quote.destination_premise_type || 2; // default Commercial

    return {
      // Quote identity
      id:                   null,          // internal booking ID — not yet created
      outside_reference_no: quote.quote_number || String(quote.id),
      quote_number:         quote.quote_number,

      // Pricing
      service_option:   serviceOption,
      est_total_price:  estTotalPrice,

      // Pickup — populated from origin_suburb / origin_postcode / origin_state
      // returned by the iDrv5 API.
      pickup_suburb:      quote.origin_suburb   || '',
      pickup_state:       quote.origin_state    || '',
      pickup_postcode:    quote.origin_postcode || '',
      pickup_full_suburb: quote.origin_suburb
        ? `${quote.origin_suburb} ${quote.origin_state || ''} ${quote.origin_postcode || ''}`.trim()
        : '',
      pickup_date:        quote.pickup_date || null,

      // Delivery — populated from destination_suburb / destination_postcode / destination_state.
      delivery_suburb:      quote.destination_suburb   || '',
      delivery_state:       quote.destination_state    || '',
      delivery_postcode:    quote.destination_postcode || '',
      delivery_full_suburb: quote.destination_suburb
        ? `${quote.destination_suburb} ${quote.destination_state || ''} ${quote.destination_postcode || ''}`.trim()
        : '',
      delivery_date:        quote.delivery_date || null,

      // Packagings
      packagings:         packagings,
      total_weight:       totalWeight,
      chargeable_pallets: chargeablePallets,

      // Flags
      is_dg:           isDg ? 1 : 0,
      is_hand_unload:  false,
      is_trailer_split: false,
      pickup_is_tail_lift: pickupIsTailLift,
      delivery_is_tail_lift: deliveryIsTailLift,
      pickup_premise_type_id: pickupPremiseTypeId,
      delivery_premise_type_id: deliveryPremiseTypeId,

      // DG class pre-fill (null when no DG goods)
      dgClass: dgClass,

      // Customer pre-fill from the iDrv5 quote
      customer: {
        company:      quote.company_name || '',
        email:        quote.email        || '',
        contact_no:   quote.mobile_no    || '',
        contact_name: quote.full_name    || ''
      },

      // Delivery business days — from the selected rate entry.
      // Used by BookingComponent.patchQuoteDataToForm() to pre-fill the
      // delivery date as today + N business days (weekends skipped).
      delivery_business_days: selectedRateEntry?.business_days ?? null,

      // Special instructions
      special_instructions: quote.special_instructions || '',

      // Original iDrv5 response for traceability
      idrv5_data: response
    };
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * iDrv5 already sends a packaging_type_id. We validate it against our known
   * set. If it is 0, null, or not in our set we return null so the form's
   * Validators.required forces the user to pick a type manually.
   *
   * Known IDs (must stay in sync with the internal packaging_types table):
   *   10 Pallet | 17 Carton | 18 Skid | 19 Oversized Plain Pallet
   *   21 Chep Pallet | 22 Oversized Chep Pallet
   *   23 Loscam Pallet | 24 Oversized Loscam Pallet
   *
   * ID Mappings (iDrv5 → internal):
   *   5 (Skid) → 18 (Skid)
   *   6 (Pallet) → 10 (Pallet)
   */
  private resolvePackagingTypeId(pkg: Idrv5QuotePackaging): number | null {
    const KNOWN_IDS = new Set([10, 17, 18, 19, 21, 22, 23, 24]);

    // Map iDrv5 ID 5 (Skid) to internal ID 18 (Skid)
    if (pkg.packaging_type_id === 5 &&
        (pkg.packaging_type_name || '').toLowerCase().includes('skid')) {
      return 18;
    }

    // Map iDrv5 ID 1 (Carton / Box) to internal ID 17 (Carton)
    if (pkg.packaging_type_id === 1 &&
        (pkg.packaging_type_name || '').toLowerCase().includes('carton')) {
      return 17;
    }

    if (pkg.packaging_type_id && KNOWN_IDS.has(pkg.packaging_type_id)) {
      return pkg.packaging_type_id;
    }

    // iDrv5 uses id=6 for "Pallet" in their own system — map it to our id=10
    if (pkg.packaging_type_id === 6 &&
        (pkg.packaging_type_name || '').toLowerCase().includes('pallet')) {
      return 10;
    }

    // Unknown — force user to select
    return null;
  }

  /**
   * Chargeable pallets formula (identical to DyspachMapperService):
   *   volume_m3 = (l/100) × (w/100) × (h/100)
   *   cubicWeight = volume_m3 × 333
   *   chargeWeight = max(physicalWeight, cubicWeight)
   *   chargeablePallets = chargeWeight × qty / 1000
   */
  private calculateChargeablePallets(
    lengthCm: number,
    widthCm: number,
    heightCm: number,
    weightKg: number,
    qty: number
  ): number {
    const physicalWeight = weightKg * qty;
    const volume_m3 = (lengthCm / 100) * (widthCm / 100) * (heightCm / 100);
    const cubicWeight = volume_m3 * 333;
    const chargeWeight = Math.max(physicalWeight, cubicWeight);
    return (chargeWeight * qty) / 1000;
  }

  /**
   * Map iDrv5 service level fields to the internal service_option string.
   *
   * ID-based mapping (preferred):
   *   1  = Express   → 'express'
   *   2  = Standard  → 'standard'
   *   3  = Economy   → 'economy'
   *   11 = Hot Shot  → 'hot shot'
   *
   * Falls back to name/description keyword matching when the ID is unknown,
   * then defaults to 'economy'.
   */
  private getServiceOption(
    serviceLevelId?: number,
    serviceLevelName?: string | null,
    serviceLevelDescription?: string
  ): string {
    // --- ID-based mapping ---
    switch (serviceLevelId) {
      case 1:  return 'express';
      case 2:  return 'standard';
      case 3:  return 'economy';
      case 11: return 'hot shot';
    }

    // --- Name / description keyword fallback ---
    const label = ((serviceLevelName || '') + ' ' + (serviceLevelDescription || '')).toLowerCase();

    if (label.includes('hot shot') || label.includes('hotshot')) return 'hot shot';
    if (label.includes('standard')) return 'standard';
    if (label.includes('express') || label.includes('expedited')) return 'express';

    // Default
    console.warn(
      `iDrv5: unrecognised service_level_id=${serviceLevelId} name="${serviceLevelName}" — ` +
      `defaulting to economy`
    );
    return 'economy';
  }
}
